import React from 'react';
import moment from 'moment';

/**
 * Reporte Picking List en PDF con react-pdf.
 *
 * Los datos vienen de /api/pedido/pedidoPicking
 * (SELECT * FROM fn_reporte_picking($1::jsonb, $2::integer)):
 *
 *   codproducto               codigo del producto
 *   nomproducto               nombre del producto
 *   cantidad_total_principal  cantidad pedida en la unidad principal, ej "5.00B"
 *                             (tipounidad = 0; si no aplica viene "0.00")
 *   cantidad_total_unidades   cantidad pedida en unidades, ej "6.00U"
 *                             (tipounidad = 1; si no aplica viene "0.00")
 *   cantidad_existencia       existencia total en unidades (ej. 584.00)
 *   cantidad_principal        existencia en la unidad principal (bultos, cajas...)
 *   cantidad_unidades         existencia sobrante en unidades
 *   unidad                    nombre de la unidad pedida (BULTO, CAJAS, UNIDAD...)
 *   orden_picking             orden sugerido de picking
 *   observacion               observacion del renglon del pedido
 *
 * react-pdf se carga en forma diferida desde public/lib/reactpdf.bundle.js
 * (ver tools/reactpdf) porque el webpack 1 de react-scripts 0.9.5 no puede
 * parsear la sintaxis moderna con la que se publica la libreria.
 */

const RUTA_BUNDLE = (process.env.PUBLIC_URL || '') + '/lib/reactpdf.bundle.js';

// ---- continuacion ----

let cargaBundle = null;

export const cargarReactPDF = () => {
  if (typeof window !== 'undefined' && window.ReactPDF) {
    return Promise.resolve(window.ReactPDF);
  }

  if (cargaBundle) return cargaBundle;

  cargaBundle = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RUTA_BUNDLE;
    script.async = true;
    script.onload = () => {
      if (window.ReactPDF) {
        resolve(window.ReactPDF);
      } else {
        cargaBundle = null;
        reject(new Error('No se pudo inicializar react-pdf'));
      }
    };
    script.onerror = () => {
      cargaBundle = null;
      reject(new Error('No se pudo cargar la libreria de PDF (react-pdf)'));
    };
    document.body.appendChild(script);
  });

  return cargaBundle;
};

// Campos del API usados por el reporte (con alias por si cambian de nombre)
const CAMPOS = {
  orden: ['orden_picking', 'ordenpicking', 'orden'],
  codigo: ['codproducto', 'codigoproducto', 'codigo'],
  nombre: ['nomproducto', 'nombreproducto', 'nombre', 'descripcion'],
  pedida: [
    'cantidad_total',
    'cantidad_total_principal',
    'cantidad_pedida_principal',
    'cantidad_pedida',
  ],
  existenciaTotal: ['cantidad_existencia', 'existencia_total'],
  principal: ['cantidad_principal', 'cantidadprincipal', 'existencia_principal'],
  unidades: ['cantidad_unidades', 'cantidadunidades', 'unidades'],
  unidadPrincipal: ['unidadprincipal', 'unidad_principal', 'letraunidad'],
  tipoUnidad: ['tipounidad', 'tipo_unidad'],
  unidad: ['unidad', 'unidadmedida', 'nomunidad'],
  observacion: ['observacion', 'observaciones', 'nota'],
};

const texto = (valor) => {
  if (valor === undefined || valor === null) return '';
  if (valor instanceof Date) return moment(valor).format('DD-MM-YYYY');
  if (typeof valor === 'object') {
    try {
      return JSON.stringify(valor);
    } catch (e) {
      return '';
    }
  }
  return String(valor);
};

const numero = (valor) => {
  if (valor === undefined || valor === null) return null;
  // acepta numeros y textos con la unidad al final, ej. "5.00B" o "6.00U"
  const n = parseFloat(String(valor).trim());
  return isNaN(n) ? null : n;
};

const conValor = (valor) => valor !== undefined && valor !== null && String(valor).trim() !== '';

// el backend usa el prefijo txt en algunas funciones de reporte (txtproducto, ...)
const sinPrefijo = (clave) => String(clave).toLowerCase().replace(/^txt/, '');

const indexarFila = (fila) => {
  const indice = {};
  Object.keys(fila).forEach((clave) => {
    const normal = sinPrefijo(clave);
    if (indice[normal] === undefined) indice[normal] = clave;
  });
  return indice;
};

const valorCampo = (fila, indice, claves) => {
  for (let i = 0; i < claves.length; i++) {
    const clave = indice[claves[i]];
    if (clave !== undefined && conValor(fila[clave])) return fila[clave];
  }
  return null;
};

const formatearCantidad = (valor) => {
  const n = numero(valor);
  if (n === null) return '';
  return n
    .toFixed(2)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
};

const LETRAS_UNIDAD = [
  { prueba: /bult/i, letra: 'B' },
  { prueba: /caj/i, letra: 'C' },
  { prueba: /paq/i, letra: 'P' },
  { prueba: /docen/i, letra: 'D' },
  { prueba: /unid/i, letra: 'U' },
];

export const letraUnidad = (unidad) => {
  const nombre = texto(unidad).trim();
  for (let i = 0; i < LETRAS_UNIDAD.length; i++) {
    if (LETRAS_UNIDAD[i].prueba.test(nombre)) return LETRAS_UNIDAD[i].letra;
  }
  return nombre === '' ? 'U' : nombre.substring(0, 1).toUpperCase();
};

/**
 * Existencia en formato "104B-21U": cantidad principal + letra + unidades.
 * unidad puede ser el nombre ("BULTO") o directamente la letra ("B").
 */
export const existenciaTexto = (principal, unidades, unidad) => {
  const p = numero(principal);
  const u = numero(unidades);
  if (p === null && u === null) return '';
  if (p === null) return formatearCantidad(u) + 'U';

  const letra = letraUnidad(unidad);
  const sueltas = u === null ? 0 : u;
  return formatearCantidad(p) + letra + '-' + formatearCantidad(sueltas) + 'U';
};

/**
 * Lee cantidades del API del tipo "15B-10U", "5.00B", "6.00U" o 5.
 * Devuelve { principal, unidades, letra, modo } o null.
 */
export const parsearCantidadBU = (valor) => {
  if (valor === undefined || valor === null) return null;
  if (typeof valor === 'number') {
    return { principal: valor, unidades: 0, letra: '', modo: 'numero' };
  }

  const limpio = String(valor).trim();
  if (limpio === '') return null;

  const numeros = limpio.match(/\d+(?:[.,]\d+)?/g) || [];
  if (numeros.length === 0) return null;

  const letras = limpio.match(/[A-Za-z]/g) || [];
  const letra = letras.length > 0 ? letras[0].toUpperCase() : '';
  const aNumero = (t) => parseFloat(t.replace(',', '.'));

  if (numeros.length === 1) {
    if (letra === 'U') {
      return { principal: 0, unidades: aNumero(numeros[0]), letra: letra, modo: 'unidades' };
    }
    return {
      principal: aNumero(numeros[0]),
      unidades: 0,
      letra: letra,
      modo: letra === '' ? 'numero' : 'principal',
    };
  }

  return {
    principal: aNumero(numeros[0]),
    unidades: aNumero(numeros[1]),
    letra: letra,
    modo: 'bu',
  };
};

export const textoCantidad = (partes, letra, tipoUnidad) => {
  if (!partes) return '';

  const letraUsar = partes.letra || letra;
  if (partes.modo === 'bu') {
    return (
      formatearCantidad(partes.principal) +
      letraUsar +
      '-' +
      formatearCantidad(partes.unidades) +
      'U'
    );
  }
  if (partes.modo === 'unidades') return formatearCantidad(partes.unidades) + 'U';
  if (partes.modo === 'principal') return formatearCantidad(partes.principal) + letraUsar;
  // numero sin letra (la funcion devuelve '0' cuando no hay cantidad)
  if (partes.principal === 0) return '0';
  return tipoUnidad === 1
    ? formatearCantidad(partes.principal) + 'U'
    : formatearCantidad(partes.principal) + letraUsar;
};

/**
 * Unidades por empaque deducidas de la existencia total
 * (ej. 2517 total, 104 bultos, 21 unidades => 24 unidades por bulto).
 */
const factorExistencia = (principal, unidades, total) => {
  if (total === null || principal === null || principal <= 0) return null;
  const factor = (total - (unidades === null ? 0 : unidades)) / principal;
  return factor > 0 ? factor : null;
};

/**
 * Cantidad sugerida de picking = faltante para cubrir lo pedido
 * (cantidad pedida - existencia). null = no falta producto.
 */
export const cantidadSugerida = (pedida, principal, unidades, letra, existenciaTotal) => {
  const partes = pedida && typeof pedida === 'object' ? pedida : parsearCantidadBU(pedida);
  if (!partes) return null;

  const exB = principal === null ? 0 : principal;
  const exU = unidades === null ? 0 : unidades;
  const total = existenciaTotal === undefined ? null : existenciaTotal;
  const factor = factorExistencia(principal, unidades, total);

  // pidieron en unidades: se compara contra la existencia total
  if (partes.modo === 'unidades') {
    if (total === null) return null;
    const faltaTotal = partes.unidades - total;
    if (faltaTotal <= 0) return null;

    if (factor !== null) {
      const faltaB = Math.floor(faltaTotal / factor);
      const faltaU = faltaTotal - faltaB * factor;
      if (faltaB > 0) {
        return (
          formatearCantidad(faltaB) +
          letra +
          (faltaU > 0 ? '-' + formatearCantidad(faltaU) + 'U' : '')
        );
      }
      return formatearCantidad(faltaU) + 'U';
    }
    return formatearCantidad(faltaTotal) + 'U';
  }

  // pidieron en la unidad principal
  let faltaB = partes.principal - exB;
  let faltaU = partes.unidades - exU;

  // si faltan unidades se toma prestado un empaque completo
  if (faltaU < 0 && factor !== null) {
    const prestamos = Math.ceil(-faltaU / factor);
    faltaB -= prestamos;
    faltaU += prestamos * factor;
  }
  if (faltaU < 0) faltaU = 0;
  if (faltaB < 0 || (faltaB === 0 && faltaU === 0)) return null;

  const letraUsar = partes.letra || letra;
  if (faltaB <= 0) return formatearCantidad(faltaU) + 'U';

  return (
    formatearCantidad(faltaB) +
    letraUsar +
    (faltaU > 0 ? '-' + formatearCantidad(faltaU) + 'U' : '')
  );
};

/**
 * Normaliza las filas del API a los renglones del reporte.
 */
export const normalizarPicking = (datos) => {
  const entrada = Array.isArray(datos) ? datos : [];

  const filas = entrada.map((registro) => {
    const fila = registro || {};
    const indice = indexarFila(fila);
    const unidad = texto(valorCampo(fila, indice, CAMPOS.unidad));
    const letraDato = texto(valorCampo(fila, indice, CAMPOS.unidadPrincipal)).trim();
    const letra = letraDato !== '' ? letraDato.toUpperCase() : letraUnidad(unidad);
    const tipoUnidad = numero(valorCampo(fila, indice, CAMPOS.tipoUnidad));
    const existenciaPrincipal = numero(valorCampo(fila, indice, CAMPOS.principal));
    const existenciaUnidades = numero(valorCampo(fila, indice, CAMPOS.unidades));
    const existenciaTotal = numero(valorCampo(fila, indice, CAMPOS.existenciaTotal));
    const partesPedida = parsearCantidadBU(valorCampo(fila, indice, CAMPOS.pedida));
    const orden = numero(valorCampo(fila, indice, CAMPOS.orden));

    // cuando el producto no tiene valorfactor la funcion devuelve el desglose
    // de existencia en NULL: en ese caso se muestra la existencia total
    const tieneDesglose = existenciaPrincipal !== null || existenciaUnidades !== null;

    let existencia = existenciaTexto(existenciaPrincipal, existenciaUnidades, letra);
    if (existencia === '' && existenciaTotal !== null) {
      existencia = formatearCantidad(existenciaTotal) + 'U';
    }

    return {
      orden: orden,
      codigo: texto(valorCampo(fila, indice, CAMPOS.codigo)),
      nombre: texto(valorCampo(fila, indice, CAMPOS.nombre)),
      unidad: unidad,
      pedida: textoCantidad(partesPedida, letra, tipoUnidad),
      existencia: existencia,
      sugerida: tieneDesglose
        ? cantidadSugerida(
          partesPedida,
          existenciaPrincipal,
          existenciaUnidades,
          letra,
          existenciaTotal
        )
        : null,
      observacion: texto(valorCampo(fila, indice, CAMPOS.observacion)),
    };
  });

  filas.sort((a, b) => {
    const oa = a.orden === null ? 0 : a.orden;
    const ob = b.orden === null ? 0 : b.orden;
    if (oa !== ob) return oa - ob;
    return a.codigo < b.codigo ? -1 : a.codigo > b.codigo ? 1 : 0;
  });

  let hayObservacion = false;
  let conFaltante = 0;
  filas.forEach((fila) => {
    if (fila.observacion !== '') hayObservacion = true;
    if (fila.sugerida !== null) conFaltante++;
  });

  return { filas: filas, hayObservacion: hayObservacion, conFaltante: conFaltante };
};

const crearEstilos = (StyleSheet) =>
  StyleSheet.create({
    pagina: {
      paddingTop: 30,
      paddingBottom: 34,
      paddingHorizontal: 24,
      fontSize: 8,
      fontFamily: 'Helvetica',
      color: '#232323',
    },
    encabezado: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      borderBottomWidth: 1.5,
      borderBottomColor: '#0b3d63',
      paddingBottom: 5,
    },
    titulo: {
      fontSize: 15,
      fontFamily: 'Helvetica-Bold',
      color: '#0b3d63',
    },
    subtitulo: {
      fontSize: 8,
      color: '#4d4d4d',
    },
    resumen: {
      borderWidth: 1,
      borderColor: '#d5dee6',
      backgroundColor: '#f2f6fa',
      paddingVertical: 4,
      paddingHorizontal: 6,
      marginBottom: 8,
    },
    resumenFila: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    resumenTexto: {
      fontSize: 8,
      fontFamily: 'Helvetica-Bold',
      color: '#0b3d63',
    },
    resumenPedidos: {
      fontSize: 7.5,
      color: '#4d4d4d',
      marginTop: 2,
    },
    filaTitulos: {
      flexDirection: 'row',
      backgroundColor: '#0b3d63',
    },
    tituloColumna: {
      fontSize: 7.5,
      fontFamily: 'Helvetica-Bold',
      color: '#ffffff',
      paddingVertical: 4,
      paddingHorizontal: 3,
    },
    fila: {
      flexDirection: 'row',
      borderBottomWidth: 0.5,
      borderBottomColor: '#dfe6ec',
    },
    filaPar: {
      backgroundColor: '#f7f9fb',
    },
    celda: {
      fontSize: 7.5,
      paddingVertical: 3,
      paddingHorizontal: 3,
    },
    centrado: {
      textAlign: 'center',
    },
    derecha: {
      textAlign: 'right',
    },
    faltante: {
      fontFamily: 'Helvetica-Bold',
      color: '#b00020',
    },
    firmas: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 28,
    },
    firma: {
      width: '30%',
      borderTopWidth: 0.8,
      borderTopColor: '#8a99a8',
      paddingTop: 3,
    },
    firmaTexto: {
      fontSize: 7,
      color: '#5c6b7a',
      textAlign: 'center',
    },
    pieFijo: {
      position: 'absolute',
      bottom: 14,
      left: 24,
      right: 24,
      flexDirection: 'row',
      justifyContent: 'space-between',
      borderTopWidth: 0.5,
      borderTopColor: '#d5dee6',
      paddingTop: 3,
    },
    pieTexto: {
      fontSize: 7,
      color: '#6c757d',
    },
    sinDatos: {
      marginTop: 40,
      textAlign: 'center',
      fontSize: 10,
      color: '#6c757d',
    },
  });

/**
 * Documento react-pdf del picking. R = window.ReactPDF
 *
 * datos     : filas devueltas por /api/pedido/pedidoPicking
 * metadatos : { empresa, usuario, titulo, pedidos, esNota }
 *
 * Para notas de entrega el reporte solo muestra las columnas
 * ORDEN PICKING, CODIGO, PRODUCTO y CANT. A DESPACHAR.
 */
export const crearDocumentoPicking = (R, datos, metadatos) => {
  const { Document, Page, View, Text } = R;
  const estilos = crearEstilos(R.StyleSheet);
  const info = metadatos || {};
  const etiquetaDocumentos = texto(info.etiquetaDocumentos || 'PEDIDOS').toUpperCase();
  const esNota = info.esNota === true;
  const { filas, hayObservacion, conFaltante } = normalizarPicking(datos);

  const pedidos = (Array.isArray(info.pedidos) ? info.pedidos : []).filter(conValor);
  const maxPedidosResumen = 20;
  let resumenPedidos = '0';
  if (pedidos.length > 0 && pedidos.length <= maxPedidosResumen) {
    resumenPedidos = pedidos.join(', ');
  } else if (pedidos.length > maxPedidosResumen) {
    resumenPedidos =
      pedidos.slice(0, maxPedidosResumen).join(', ') +
      ' (+' +
      (pedidos.length - maxPedidosResumen) +
      ')';
  }

  return (
    <Document
      title="Picking List"
      author={texto(info.empresa)}
      subject="Reporte Picking List"
      creator="ATHOS ERP"
      producer="ATHOS ERP"
    >
      <Page size="A4" orientation="portrait" style={estilos.pagina}>
        <View style={estilos.encabezado} fixed>
          <View>
            <Text style={estilos.titulo}>PICKING LIST</Text>
            <Text style={estilos.subtitulo}>{texto(info.empresa)}</Text>
          </View>
          <View>
            <Text style={estilos.subtitulo}>
              {'Emitido: ' + moment().format('DD-MM-YYYY hh:mm a')}
            </Text>
            <Text style={estilos.subtitulo}>{'Usuario: ' + texto(info.usuario)}</Text>
          </View>
        </View>

        <View style={estilos.resumen}>
          <View style={estilos.resumenFila}>
            <Text style={estilos.resumenTexto}>{'PRODUCTOS: ' + filas.length}</Text>
            {!esNota && (
              <Text style={estilos.resumenTexto}>{'CON FALTANTE: ' + conFaltante}</Text>
            )}
          </View>
          <Text style={estilos.resumenPedidos}>{etiquetaDocumentos + ': ' + resumenPedidos}</Text>
        </View>

        <View style={estilos.filaTitulos}>
          <Text style={[estilos.tituloColumna, { flex: 0.8 }, estilos.centrado]}>
            ORDEN PICKING
          </Text>
          <Text style={[estilos.tituloColumna, { flex: 1.1 }]}>CODIGO</Text>
          <Text style={[estilos.tituloColumna, { flex: 3.7 }]}>PRODUCTO</Text>
          <Text
            style={[
              estilos.tituloColumna,
              { flex: esNota ? 1.35 : 1.05 },
              estilos.derecha,
            ]}
          >
            {esNota ? 'CANT. A DESPACHAR' : 'CANT. PEDIDA'}
          </Text>
          {!esNota && (
            <Text style={[estilos.tituloColumna, { flex: 1.25 }, estilos.centrado]}>
              EXISTENCIA
            </Text>
          )}
          {!esNota && (
            <Text style={[estilos.tituloColumna, { flex: 1.35 }, estilos.derecha]}>
              CANT. SUGERIDA
            </Text>
          )}
          {!esNota && hayObservacion && (
            <Text style={[estilos.tituloColumna, { flex: 2 }]}>OBSERVACION</Text>
          )}
        </View>

        {filas.length === 0 && (
          <Text style={estilos.sinDatos}>No hay informacion por mostrar</Text>
        )}

        {filas.map((fila, indice) => (
          <View
            key={indice}
            style={[estilos.fila, indice % 2 === 1 ? estilos.filaPar : null]}
            wrap={false}
          >
            <Text style={[estilos.celda, { flex: 0.8 }, estilos.centrado]}>
              {fila.orden === null ? '' : fila.orden}
            </Text>
            <Text style={[estilos.celda, { flex: 1.1 }]}>{fila.codigo}</Text>
            <Text style={[estilos.celda, { flex: 3.7 }]}>{fila.nombre}</Text>
            <Text
              style={[
                estilos.celda,
                { flex: esNota ? 1.35 : 1.05 },
                estilos.derecha,
              ]}
            >
              {fila.pedida}
            </Text>
            {!esNota && (
              <Text style={[estilos.celda, { flex: 1.25 }, estilos.centrado]}>
                {fila.existencia}
              </Text>
            )}
            {!esNota && (
              <Text
                style={[
                  estilos.celda,
                  { flex: 1.35 },
                  estilos.derecha,
                  fila.sugerida !== null ? estilos.faltante : null,
                ]}
              >
                {fila.sugerida === null ? '-' : fila.sugerida}
              </Text>
            )}
            {!esNota && hayObservacion && (
              <Text style={[estilos.celda, { flex: 2 }]}>{fila.observacion}</Text>
            )}
          </View>
        ))}
        <View style={estilos.firmas} wrap={false}>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>PREPARADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>VERIFICADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>RECIBIDO POR</Text>
          </View>
        </View>

        <View style={estilos.pieFijo} fixed>
          <Text style={estilos.pieTexto}>
            {texto(info.titulo) + ' | B: bultos, C: cajas, P: paquetes, U: unidades'}
          </Text>
          <Text
            style={estilos.pieTexto}
            render={({ pageNumber, totalPages }) =>
              'Pagina ' + pageNumber + ' de ' + totalPages
            }
          />
        </View>
      </Page>
    </Document>
  );
};

/**
 * Genera el PDF y lo muestra en la ventana indicada (si no se pudo abrir,
 * lo descarga).
 */
export const imprimirReportePicking = async (datos, metadatos, ventana) => {
  const R = await cargarReactPDF();
  const documento = crearDocumentoPicking(R, datos, metadatos);
  const blob = await R.pdf(documento).toBlob();
  const url = URL.createObjectURL(blob);

  if (ventana && !ventana.closed) {
    ventana.location.href = url;
  } else {
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = 'Picking-' + moment().format('DD-MM-YYYY-hhmm') + '.pdf';
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
  }

  setTimeout(() => URL.revokeObjectURL(url), 120000);
};



// WEBPACK FOOTER //
// ./src/components/ventas/reportePickingPDF.js